import React, {
  Suspense,
  useContext,
  isValidElement,
  useCallback,
} from 'react';
import DesktopAppBarMenu from './components/DesktopAppBarMenu';
import { ScreenSizeConfiguratorContext } from '../../../../library/shared-ui/components/ScreenSizeConfigurator';
import { Typography } from '@sezzle/sezzle-mui';
import { NavigationBarAndMenuProps } from './types';
import {
  StyledAppBar,
  StyledToolBar,
  StyledAppBarLogoWrapper,
  StyledAppBarLogoContainer,
  StyledAppBarWebButtonsContentContainer,
  StyledAppBarContainer,
} from './styles';
import { GatsbyImage, getImage } from 'gatsby-plugin-image';
import { SearchMenu } from './components/SearchMenu';
import HamburgerMenu from './components/HamburgerMenu';

const NavigationBarAndMenu = ({
  featuredLinksMobileMenu,
  mobileNavigationLinks,
  featuredMobileSpot,
  ctaButtons,
  logo,
  navigationLinks,
  showSearchSuggestions,
  mobileShopMenu,
  searchSuggestions,
  mobileButton,
  overrideStyles = {},
}: NavigationBarAndMenuProps) => {
  const { screenConfig } = useContext(ScreenSizeConfiguratorContext);

  const renderMenuIcon = () => {
    if (
      featuredLinksMobileMenu?.length > 0 ||
      mobileNavigationLinks?.length > 0 ||
      featuredMobileSpot ||
      ctaButtons?.length > 0
    ) {
      return (
        <HamburgerMenu
          featuredLinksMobileMenu={featuredLinksMobileMenu}
          mobileNavigationLinks={mobileNavigationLinks}
          featuredMobileSpot={featuredMobileSpot}
          ctaButtons={ctaButtons}
          screenConfig={screenConfig}
        />
      );
    }

    return null;
  };

  const LogoWrapper = useCallback(
    ({ children, href }: { children: React.ReactNode; href: string }) => (
      <StyledAppBarLogoWrapper>
        <StyledAppBarLogoContainer>
          <a href={href}>{children}</a>
        </StyledAppBarLogoContainer>
      </StyledAppBarLogoWrapper>
    ),
    []
  );

  const renderLogo = () => {
    // If the logo is a static React element (usually SVG logo)
    // no need to do further processing
    if (isValidElement(logo)) {
      return <LogoWrapper href={'/'}>{logo}</LogoWrapper>;
    }

    const logoUrl = logo?.imageUrl?.url || logo?.url;
    const assistiveText = logo?.assistiveText;

    const img = getImage(logo?.image?.image?.gatsbyImageData);

    if (logoUrl) {
      return (
        <LogoWrapper href={logoUrl}>
          <GatsbyImage
            image={img}
            loading="eager"
            alt="Go to home page. Sezzle logo."
            objectFit="contain"
          />
        </LogoWrapper>
      );
    }
    return <Typography>{assistiveText}</Typography>;
  };

  const renderSearchIcon = () => {
    if (showSearchSuggestions) {
      return (
        <SearchMenu
          fields={mobileShopMenu}
          searchSuggestions={searchSuggestions}
          mobileButton={mobileButton}
          screenConfig={screenConfig}
        />
      );
    }
    return null;
  };

  const renderNavigationBar = () => {
    return (
      <StyledAppBarWebButtonsContentContainer>
        {showSearchSuggestions && (
          <Suspense fallback={<div />}>{searchSuggestions}</Suspense>
        )}
        <DesktopAppBarMenu
          navigationLinks={navigationLinks}
          ctaButtons={ctaButtons}
        />
      </StyledAppBarWebButtonsContentContainer>
    );
  };

  return (
    <StyledAppBar
      position="sticky"
      color="inherit"
      style={overrideStyles?.appBar}
    >
      <StyledToolBar>
        <StyledAppBarContainer>
          {renderMenuIcon()}
          {renderLogo()}
          {renderNavigationBar()}
          {renderSearchIcon()}
        </StyledAppBarContainer>
      </StyledToolBar>
    </StyledAppBar>
  );
};

export default React.memo(NavigationBarAndMenu);
